import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight, Check } from "lucide-react";
import SeoHead from "../../components/SeoHead";

/**
 * Shared SEO landing template.
 *
 * Every landing supplies its own `data` object (see `landingsData.js`).
 * Copy is provided in both languages inside the data object so the page
 * itself stays pure and predictable — no per-key JSON lookups spread across
 * multiple locale files, which would fragment SEO-critical content.
 *
 * User-generated content is NOT rendered here.  These pages carry
 * marketing/product copy that is authored by the Mytharion team and safe to
 * translate.
 *
 * `data` shape:
 *   {
 *     seoTitle: {en, it},
 *     seoDescription: {en, it},
 *     path: "/dnd-map-maker",
 *     eyebrow: {en, it},
 *     h1: {en, it},
 *     lead: {en, it},   // Rich supported via array of paragraphs
 *     primaryCta: {href, label: {en, it}},
 *     secondaryCta: {href, label: {en, it}},
 *     features: [{title:{en,it}, desc:{en,it}, icon:Component}],
 *     how: [{title:{en,it}, desc:{en,it}}],
 *     faq: [{q:{en,it}, a:{en,it}}],
 *   }
 */
export default function SeoLandingTemplate({ data }) {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "en").startsWith("it") ? "it" : "en";
  const T = (obj) => (obj && obj[lang]) || (obj && obj.en) || "";

  const paragraphs = Array.isArray(data.lead) ? data.lead : [data.lead];

  const faqJsonLd = data.faq && data.faq.length > 0
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: data.faq.map((f) => ({
          "@type": "Question",
          name: T(f.q),
          acceptedAnswer: { "@type": "Answer", text: T(f.a) },
        })),
      }
    : null;

  const softwareJsonLd = data.software
    ? {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: data.software.name || "Mytharion",
        applicationCategory: data.software.category || "GameApplication",
        operatingSystem: "Web",
        description: T(data.seoDescription),
        url: `https://mytharion.app${data.path}`,
        publisher: {
          "@type": "Organization",
          name: "Mytharion",
          url: "https://mytharion.app/",
        },
      }
    : null;

  const jsonLd = faqJsonLd && softwareJsonLd
    ? { "@context": "https://schema.org", "@graph": [softwareJsonLd, faqJsonLd] }
    : faqJsonLd || softwareJsonLd;

  return (
    <>
      <SeoHead
        title={T(data.seoTitle)}
        description={T(data.seoDescription)}
        path={data.path}
        jsonLd={jsonLd}
      />
      <div className="container py-16 space-y-16 max-w-5xl mx-auto px-6">
        {/* HERO */}
        <header className="max-w-3xl">
          <p className="text-xs font-heading uppercase tracking-widest text-arcane mb-3">
            {T(data.eyebrow)}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-gold mb-4">
            {T(data.h1)}
          </h1>
          {paragraphs.map((p, i) => (
            <p key={i} className="text-base text-parchment/90 mb-4 leading-relaxed">
              {T(p)}
            </p>
          ))}
          <div className="flex flex-wrap gap-3 mt-6">
            {data.primaryCta && (
              <Link
                to={data.primaryCta.href}
                data-testid={`landing-${data.slug}-cta-primary`}
                className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-gradient-to-b from-yellow-600 to-yellow-800 text-black font-heading gold-glow"
              >
                {T(data.primaryCta.label)} <ChevronRight className="w-4 h-4" />
              </Link>
            )}
            {data.secondaryCta && (
              <Link
                to={data.secondaryCta.href}
                data-testid={`landing-${data.slug}-cta-secondary`}
                className="inline-flex items-center gap-2 h-11 px-5 rounded-md rune-border text-gold hover:bg-yellow-900/20 font-heading"
              >
                {T(data.secondaryCta.label)}
              </Link>
            )}
          </div>
        </header>

        {/* FEATURES */}
        {data.features && (
          <section aria-labelledby="features">
            <h2 id="features" className="font-display text-3xl text-gold mb-6">
              {T(data.featuresTitle) || (lang === "it" ? "Cosa puoi fare" : "What you can do")}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.features.map((f, i) => {
                const Icon = f.icon;
                return (
                  <div key={i} className="rune-border p-5 bg-card/60 rounded-lg">
                    {Icon && <Icon className="w-6 h-6 text-gold mb-3" />}
                    <h3 className="font-heading text-base text-parchment mb-1">
                      {T(f.title)}
                    </h3>
                    <p className="text-sm text-muted-foreground">{T(f.desc)}</p>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* HOW IT WORKS */}
        {data.how && (
          <section aria-labelledby="how">
            <h2 id="how" className="font-display text-3xl text-gold mb-6">
              {T(data.howTitle) || (lang === "it" ? "Come funziona" : "How it works")}
            </h2>
            <ol className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {data.how.map((h, i) => (
                <li
                  key={i}
                  className="rune-border p-5 bg-card/60 rounded-lg relative"
                >
                  <span className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-gold text-black font-heading text-sm flex items-center justify-center gold-glow">
                    {i + 1}
                  </span>
                  <h3 className="font-heading text-base text-parchment mb-1 mt-2">
                    {T(h.title)}
                  </h3>
                  <p className="text-sm text-muted-foreground">{T(h.desc)}</p>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* BENEFITS */}
        {data.benefits && (
          <section aria-labelledby="benefits">
            <h2 id="benefits" className="font-display text-3xl text-gold mb-6">
              {T(data.benefitsTitle) || (lang === "it" ? "Vantaggi" : "Benefits")}
            </h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                  <span className="text-parchment">{T(b)}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* FAQ */}
        {data.faq && (
          <section aria-labelledby="faq">
            <h2 id="faq" className="font-display text-3xl text-gold mb-6">
              {lang === "it" ? "Domande frequenti" : "Frequently asked questions"}
            </h2>
            <div className="space-y-3">
              {data.faq.map((item, i) => (
                <details
                  key={i}
                  className="rune-border p-4 bg-card/60 rounded-lg group"
                >
                  <summary className="font-heading text-parchment cursor-pointer marker:text-gold">
                    {T(item.q)}
                  </summary>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                    {T(item.a)}
                  </p>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* FINAL CTA */}
        {data.finalCta && (
          <section className="text-center py-8">
            <p className="text-parchment/90 mb-4 text-lg">
              {T(data.finalCta.headline)}
            </p>
            <Link
              to={data.finalCta.href}
              data-testid={`landing-${data.slug}-cta-final`}
              className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-gradient-to-b from-yellow-600 to-yellow-800 text-black font-heading gold-glow"
            >
              {T(data.finalCta.label)} <ChevronRight className="w-4 h-4" />
            </Link>
          </section>
        )}
      </div>
    </>
  );
}
